import React, { useCallback, useState } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import { exportSectionedDataToCsv, exportToXlsx, type XlsxTableSection } from "@/utils/xlsxExportHelpers";
import { buildReputationReportMeta } from "../reportExportHelpers";
import { ReputationExportMenu } from "../ReputationExportMenu";
import { useSocialCoachSession } from "@/utils";

type PeriodHealthExportButtonProps = {
  getExportSections: () => XlsxTableSection[];
  filters?: string[];
  disabled?: boolean;
};

export function PeriodHealthExportButton({ getExportSections, filters = [], disabled }: PeriodHealthExportButtonProps) {
  const [isExporting, setIsExporting] = useState(false);
  const { session } = useSocialCoachSession();
  const companyName = session?.companyName;

  const handleExport = useCallback(
    async (formatType: "xlsx" | "csv") => {
      setIsExporting(true);
      try {
        const sections = getExportSections().filter(section => section.rows.length > 0);
        if (!sections.length) {
          toast.info("No performance data to export");
          return;
        }

        const totalRecords = sections.reduce((sum, section) => sum + section.rows.length, 0);
        const meta = buildReputationReportMeta({
          reportName: "SocialCoach Performance Report",
          description: "Metric card breakdown for the selected reputation overview scope and period.",
          companyName,
          filters,
          totalRecords,
        });
        const filename = `Performance_${format(new Date(), "yyyy-MM-dd")}`;

        if (formatType === "csv") {
          exportSectionedDataToCsv(filename, meta, sections);
        } else {
          await exportToXlsx(filename, [{ name: "Performance", meta, sections }]);
        }

        toast.success("Exported performance data");
      } catch {
        toast.error("Failed to export performance data. Please try again.");
      } finally {
        setIsExporting(false);
      }
    },
    [companyName, filters, getExportSections]
  );

  return <ReputationExportMenu label="Export" isExporting={isExporting} disabled={disabled} onExport={handleExport} />;
}
